import analytics from "@/utils/analytics";
import { useThemeContext } from "@cohesive/ui-components";
import { useCallback } from "react";

/**
 * Newest toggle wins: an older toggle's release must not strip the class
 * out from under a newer swap still waiting to paint.
 */
let suppressionToken = 0;

export function useToggleTheme() {
  const { mode, setMode } = useThemeContext();

  const toggleTheme = useCallback(() => {
    const newMode = mode === "light" ? "dark" : "light";
    // `disable-transitions` keeps the swap from tweening every themed color.
    // While it is on, EVERY CSS transition in the app is dead (keyframe
    // animations survive) — so it must come off the moment the new theme has
    // painted, not on a long timer. Double rAF lands exactly one frame after
    // that paint; the timeout is the fallback for backgrounded tabs, where
    // rAF never fires. A stuck class here is what "expand/collapse randomly
    // snaps until refresh" looks like.
    const token = ++suppressionToken;
    const release = () => {
      if (token === suppressionToken) {
        document.body.classList.remove("disable-transitions");
      }
    };
    document.body.classList.add("disable-transitions");
    setMode(newMode);
    requestAnimationFrame(() => requestAnimationFrame(release));
    window.setTimeout(release, 300);
    analytics.trackLightDarkModeToggle({ mode: newMode });
  }, [mode, setMode]);

  return { toggleTheme };
}
